import React, { Children } from 'react';
import { ArrowLeft } from 'lucide-react';
export function PrivacyPage() {
  return <div className="min-h-screen w-full bg-[#FAF8F5] font-sans text-[#3A3A3A]" data-id="element-250">
      <div className="max-w-3xl mx-auto px-6 py-12 md:py-20" data-id="element-251">
        {/* Back Link */}
        <a href="/" className="inline-flex items-center gap-2 text-[#2D4A3E] hover:text-[#2D4A3E]/80 transition-colors mb-8" data-id="element-252">
          <ArrowLeft className="w-4 h-4" data-id="element-253" />
          <span className="text-sm font-medium" data-id="element-254">Back to Home</span>
        </a>

        {/* Header */}
        <header className="mb-12" data-id="element-255">
          <h1 className="font-['DM_Sans'] text-4xl md:text-5xl font-medium text-[#3A3A3A] mb-4" data-id="element-256">
            Privacy Policy
          </h1>
          <p className="text-[#B8A99A] text-sm" data-id="element-257">Last updated: January 2026</p>
        </header>

        {/* Content */}
        <article className="prose prose-lg max-w-none" data-id="element-258">
          <p className="text-lg text-[#3A3A3A]/80 leading-relaxed mb-8" data-id="element-259">
            Cardkeeper ("we", "our", or "us") respects your privacy. This
            Privacy Policy explains how we collect, use, and protect your
            information when you use the Cardkeeper app and website.
          </p>

          <Section title="Information We Collect" data-id="element-260">
            <Subsection title="Information You Provide" data-id="element-261">
              <p data-id="element-262">
                <strong data-id="element-263">Email address (optional):</strong> If you choose to
                provide your email, we use it to send product updates, important
                notices, or reminders related to Cardkeeper.
              </p>
            </Subsection>

            <Subsection title="Information Stored on Your Device" data-id="element-264">
              <ul data-id="element-265">
                <li data-id="element-266">
                  Cards, images, addresses, and related metadata are stored
                  locally on your device.
                </li>
                <li data-id="element-267">
                  We do not upload or store this content on our servers in the
                  current version of the app.
                </li>
              </ul>
            </Subsection>

            <Subsection title="Automatically Collected Information" data-id="element-268">
              <p data-id="element-269">
                We may collect limited, non-identifying analytics data such as:
              </p>
              <ul data-id="element-270">
                <li data-id="element-271">App usage events</li>
                <li data-id="element-272">Basic device information (e.g. app version)</li>
              </ul>
              <p data-id="element-273">
                This data is used only to understand app performance and improve
                the product.
              </p>
            </Subsection>
          </Section>

          <Section title="How We Use Information" data-id="element-274">
            <p data-id="element-275">We use collected information to:</p>
            <ul data-id="element-276">
              <li data-id="element-277">Operate and improve the app</li>
              <li data-id="element-278">Communicate with you if you opt in to email updates</li>
              <li data-id="element-279">Understand usage patterns and fix bugs</li>
            </ul>
            <p data-id="element-280">We do not:</p>
            <ul data-id="element-281">
              <li data-id="element-282">Sell your personal data</li>
              <li data-id="element-283">Share your data with advertisers</li>
              <li data-id="element-284">Track you across other apps or websites</li>
            </ul>
          </Section>

          <Section title="Data Sharing" data-id="element-285">
            <p data-id="element-286">We do not share your personal information except:</p>
            <ul data-id="element-287">
              <li data-id="element-288">
                With service providers that help us operate the app (e.g. email
                delivery or analytics), and only as necessary
              </li>
              <li data-id="element-289">If required by law</li>
            </ul>
          </Section>

          <Section title="Data Security" data-id="element-290">
            <p data-id="element-291">
              We take reasonable measures to protect your information. Because
              most data is stored locally on your device, you control access to
              it.
            </p>
          </Section>

          <Section title="Children's Privacy" data-id="element-292">
            <p data-id="element-293">
              Cardkeeper is not intended for children under 13. We do not
              knowingly collect personal information from children.
            </p>
          </Section>

          <Section title="Your Choices" data-id="element-294">
            <ul data-id="element-295">
              <li data-id="element-296">You may use the app without providing an email address.</li>
              <li data-id="element-297">You may opt out of email communications at any time.</li>
              <li data-id="element-298">You may delete the app to remove locally stored data.</li>
            </ul>
          </Section>

          <Section title="Changes to This Policy" data-id="element-299">
            <p data-id="element-300">
              We may update this Privacy Policy from time to time. Any changes
              will be posted on our website.
            </p>
          </Section>

          <Section title="Contact Us" data-id="element-301">
            <p data-id="element-302">
              If you have questions about this Privacy Policy, contact us at:{' '}
              <a href="mailto:support@cardkeeperapp.com" className="text-[#2D4A3E] hover:underline" data-id="element-303">
                support@cardkeeperapp.com
              </a>
            </p>
          </Section>
        </article>
      </div>
    </div>;
}
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="mb-10" data-id="element-304">
      <h2 className="font-['DM_Sans'] text-2xl font-medium text-[#3A3A3A] mb-4 pb-2 border-b border-[#B8A99A]/20" data-id="element-305">
        {title}
      </h2>
      <div className="text-[#3A3A3A]/80 leading-relaxed space-y-4" data-id="element-306">
        {children}
      </div>
    </section>;
}
function Subsection({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="mb-4" data-id="element-307">
      <h3 className="font-['DM_Sans'] text-lg font-medium text-[#3A3A3A] mb-2" data-id="element-308">
        {title}
      </h3>
      <div className="text-[#3A3A3A]/80 leading-relaxed space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1" data-id="element-309">
        {children}
      </div>
    </div>;
}